import { useCallback, useDeferredValue, useId, useMemo, useState } from "react";
import { toIsoDate } from "@common/helpers/date";
import type { TaskStatus } from "@common/types/task";
import { Board } from "@design-system/Board/Board";
import { useConstant } from "@hooks/useConstant";
import type { BoardColumnDef } from "@design-system/Board/Board.types";
import type { DemoTask } from "./fixtures/demoTasks";
import { applyTaskChange, type TaskBoardChange } from "./taskBoardActions";
import { BoardPlaygroundControls } from "./BoardPlaygroundControls";
import { DEFAULT_PLAYGROUND_SETTINGS } from "./boardPlaygroundSettings";
import { BoardPlaygroundToolbar } from "./BoardPlaygroundToolbar";
import { TaskBoardBulkBar } from "./TaskBoardBulkBar";
import { TaskBoardCard } from "./TaskBoardCard";
import { TaskBoardShortcutPicker } from "./TaskBoardShortcutPicker";
import { TaskDetailsDrawer } from "./TaskDetailsDrawer";
import {
    canMoveTask,
    createTaskFilter,
    getAssignee,
    getDeliverRuleBreak,
    getEntities,
    getPerson,
    getStageTitle,
    getTaskId,
    getTaskLabel,
    getTaskLock,
} from "./taskBoardConfig";
import { createTaskComparator, getTaskSortHint } from "./taskBoardSort";
import {
    buildTaskColumns,
    getTaskAxisFields,
    getTaskLanes,
} from "./taskBoardView";
import { useTaskBoardSelection } from "./useTaskBoardSelection";
import { useTaskBoardShortcuts } from "./useTaskBoardShortcuts";
import { useTaskBoardState } from "./useTaskBoardState";
import { WorkBoardPlayground } from "./WorkBoardPlayground";
import styles from "./BoardPlayground.module.scss";

const getAddLabel = (column: BoardColumnDef) =>
    `Create task in ${column.title}`;

/**
 * Board playground: the Demo Firm close tasks with title search, a status
 * filter, the Display popover (columns by, group by, sort, card fields,
 * empty columns), drag and drop that changes what the columns and lanes show
 * (canceled tasks are locked; only done tasks reach Deliver), a details
 * drawer behind each card, a command bar for selected cards, and inline
 * editing of the fields the Controls panel marks editable: chips, a rename
 * pencil, and A, S, P, D, E and R on the focused card. The Card type toggle
 * swaps in the Work board's cards instead (see `WorkBoardPlayground`).
 */
export const BoardPlayground = () => {
    const todayDateOnly = useConstant(() => toIsoDate(new Date()));
    const [settings, setSettings] = useState(DEFAULT_PLAYGROUND_SETTINGS);
    const [isControlsOpen, setIsControlsOpen] = useState(false);
    const controlsId = useId();
    const {
        isManualOrder,
        agentDrawer,
        cardFieldPlacements,
        editableCardFieldIds,
    } = settings;
    const [search, setSearch] = useState<string | undefined>();
    const [statuses, setStatuses] = useState<TaskStatus[]>([]);
    const [openTaskId, setOpenTaskId] = useState<string | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const openTask = useCallback((task: DemoTask) => {
        setOpenTaskId(task.id);
        setIsDrawerOpen(true);
    }, []);
    const changeRules = useMemo(
        () => ({
            getLock: settings.isLockEnabled ? getTaskLock : undefined,
            getRuleBreak: settings.isDeliverRuleEnabled
                ? getDeliverRuleBreak
                : undefined,
        }),
        [settings.isLockEnabled, settings.isDeliverRuleEnabled],
    );
    const board = useTaskBoardState({
        todayDateOnly,
        isManualOrder,
        changeRules,
        onDraftCreated: openTask,
    });
    const { tasks } = board;

    // Filtering a large board re-renders it; that waits for the next idle
    // frame so the filter controls stay responsive.
    const deferredSearch = useDeferredValue(search);
    const deferredStatuses = useDeferredValue(statuses);
    const isTaskInFilters = useMemo(
        () =>
            createTaskFilter({
                search: deferredSearch,
                statuses: deferredStatuses,
            }),
        [deferredSearch, deferredStatuses],
    );
    const visibleTasks = useMemo(
        () => tasks.filter(isTaskInFilters),
        [tasks, isTaskInFilters],
    );
    const { view, viewState, updateTasks } = board;
    const visibleLanes = useMemo(
        () => getTaskLanes(view.groupBy, visibleTasks),
        [view.groupBy, visibleTasks],
    );
    const compareTasks = useMemo(
        () =>
            isManualOrder
                ? undefined
                : createTaskComparator(view.sort, todayDateOnly),
        [isManualOrder, view.sort, todayDateOnly],
    );
    const sortHint = useMemo(() => getTaskSortHint(view.sort), [view.sort]);
    const columns = useMemo(
        () =>
            buildTaskColumns({
                columnBy: view.columnBy,
                tasks,
                visibleTasks,
                isEmptyColumnsShown: viewState.isEmptyColumnsShown,
                stageAddPlacements: settings.addPlacementsByColumn,
            }),
        [
            view.columnBy,
            tasks,
            visibleTasks,
            viewState.isEmptyColumnsShown,
            settings.addPlacementsByColumn,
        ],
    );
    const selection = useTaskBoardSelection({
        visibleTasks,
        columns,
        lanes: visibleLanes,
        getPlacements: board.getPlacements,
        collapsedColumnIds: viewState.collapsedColumnIds,
        collapsedLaneIds: viewState.collapsedLaneIds,
        updateTasks,
    });
    const isHiddenByFilters = useCallback(
        (taskIds: ReadonlySet<string>, change: TaskBoardChange) =>
            applyTaskChange({
                tasks: tasks.filter(({ id }) => taskIds.has(id)),
                taskIds,
                change,
            }).some((task) => !isTaskInFilters(task)),
        [tasks, isTaskInFilters],
    );
    const shortcuts = useTaskBoardShortcuts({
        updateTasks,
        isHiddenByFilters,
        editableFields: editableCardFieldIds,
    });
    // A chip edits its own card, even inside a selection.
    const { apply: applyChange } = shortcuts;
    const changeTask = useCallback(
        (task: DemoTask, change: TaskBoardChange) =>
            applyChange(new Set([task.id]), change),
        [applyChange],
    );
    const cardFields = useMemo(
        () => new Set(viewState.cardFields),
        [viewState.cardFields],
    );
    const hiddenFields = useMemo(
        () =>
            getTaskAxisFields({
                columnBy: view.columnBy,
                groupBy: view.groupBy,
            }),
        [view.columnBy, view.groupBy],
    );

    const renderCard = useCallback(
        (task: DemoTask) => (
            <TaskBoardCard
                task={task}
                assignee={getAssignee(task)}
                createdBy={getPerson(task.createdById)}
                entities={getEntities(task)}
                fields={cardFields}
                hiddenFields={hiddenFields}
                placements={cardFieldPlacements}
                editableFields={editableCardFieldIds}
                agentDrawer={agentDrawer}
                onOpen={openTask}
                onChange={changeTask}
            />
        ),
        [
            openTask,
            changeTask,
            cardFields,
            hiddenFields,
            cardFieldPlacements,
            editableCardFieldIds,
            agentDrawer,
        ],
    );

    const openedTask = tasks.find((task) => task.id === openTaskId);

    return (
        <div className={styles.playground}>
            <BoardPlaygroundToolbar
                cardType={settings.cardType}
                onCardTypeChange={(cardType) => {
                    selection.clearSelection();
                    setSettings({ ...settings, cardType });
                }}
                search={search}
                onSearchChange={setSearch}
                statuses={statuses}
                onStatusesChange={setStatuses}
                visibleCount={visibleTasks.length}
                totalCount={tasks.length}
                controlsId={controlsId}
                isControlsOpen={isControlsOpen}
                onControlsToggle={() => setIsControlsOpen(!isControlsOpen)}
                viewState={viewState}
                onViewStateChange={board.setViewState}
                isManualOrder={isManualOrder}
            />
            <div className={styles.body}>
                <div className={styles.boardArea}>
                    {settings.cardType === "work" ? (
                        <WorkBoardPlayground settings={settings} />
                    ) : (
                        <Board
                            aria-label="Close tasks"
                            items={visibleTasks}
                            columns={columns}
                            getItemId={getTaskId}
                            getItemLabel={getTaskLabel}
                            lanes={visibleLanes}
                            getPlacements={board.getPlacements}
                            renderCard={renderCard}
                            compareItems={compareTasks}
                            sortHint={sortHint}
                            emptyColumnLabel="No tasks"
                            onMove={board.applyMove}
                            onMoveMany={board.applyMoves}
                            hasMultiValueLanes={view.groupBy === "entity"}
                            getLock={
                                settings.isLockEnabled ? getTaskLock : undefined
                            }
                            canMove={
                                settings.isDeliverRuleEnabled &&
                                view.columnBy === "stage"
                                    ? canMoveTask
                                    : undefined
                            }
                            onAddItem={board.addTask}
                            canPlaceBetween={board.canPlaceBetween}
                            canPlaceAt={board.canPlaceAt}
                            enteringItemIds={board.createdTaskIds}
                            collapsedColumnIds={viewState.collapsedColumnIds}
                            collapsedLaneIds={viewState.collapsedLaneIds}
                            onCollapsedChange={board.setCollapsed}
                            getAddLabel={getAddLabel}
                            selectedItemIds={selection.selectedTaskIds}
                            onSelectionChange={selection.setSelectedTaskIds}
                            cardShortcuts={shortcuts.cardShortcuts}
                        />
                    )}
                    {settings.cardType === "tasks" && (
                        <>
                            <TaskBoardBulkBar
                                selectedCount={selection.selectedTaskIds.size}
                                onClearSelection={selection.clearSelection}
                                onChange={selection.applyToSelection}
                            />
                            <TaskBoardShortcutPicker
                                pick={shortcuts.pick}
                                tasks={visibleTasks}
                                onApply={shortcuts.apply}
                                onClose={shortcuts.close}
                            />
                        </>
                    )}
                </div>
                {isControlsOpen && (
                    <BoardPlaygroundControls
                        id={controlsId}
                        settings={settings}
                        cardFieldIds={viewState.cardFields}
                        onSettingsChange={(nextSettings) => {
                            if (
                                nextSettings.isStressMode !==
                                settings.isStressMode
                            ) {
                                board.loadTasks(nextSettings.isStressMode);
                            }
                            setSettings(nextSettings);
                        }}
                    />
                )}
            </div>
            <TaskDetailsDrawer
                task={openedTask}
                isOpen={isDrawerOpen}
                stageTitle={getStageTitle(openedTask)}
                assignee={getAssignee(openedTask)}
                entities={getEntities(openedTask)}
                onClose={() => setIsDrawerOpen(false)}
            />
        </div>
    );
};
